import { apiPost, apiPostFile, getAuthHeadersForStream } from './api'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

export interface PhotoExtractResult {
  tag: string
  name: string
  manufacturer: string
  model: string
  serialNumber: string
  specs: Record<string, string>
}

export interface ChecklistGenerateResult {
  items: Array<{
    section: string
    description: string
    criteria: string
    responseType: 'PassFail' | 'YesNo' | 'Text' | 'Number'
    required: boolean
  }>
}

export interface ProjectInsights {
  summary: string
  risks: Array<{ level: string; description: string }>
  recommendations: string[]
  stalledItems: string[]
  health: 'good' | 'warning' | 'critical'
}

export interface DocumentQAResult {
  answer: string
  sources: Array<{ documentId: number; fileName: string }>
}

export interface ProjectProposal {
  project: {
    projectNumber: string
    projectName: string
    clientName?: string
    location?: string
    description?: string
  }
  systems: Array<{
    code: string
    name: string
    systemType?: string
    description?: string
    parentCode?: string
    equipment: Array<{
      tag: string
      name: string
      description?: string
      manufacturer?: string
      model?: string
    }>
  }>
  suggestedChecklists: Array<{
    name: string
    type: 'PFC' | 'FPT' | 'BEIC' | 'Integrated' | 'Seasonal'
    category?: string
    forSystemCode: string
  }>
}

export interface ParseDocumentResult {
  proposal: ProjectProposal
  documentPreview: string
}

export interface CreateFromProposalResult {
  projectId: number
  systemCount: number
  equipmentCount: number
  templateCount: number
}

export interface ChatStreamController {
  onMessage: (callback: (text: string) => void) => void
  onDone: (callback: () => void) => void
  onError: (callback: (error: Error) => void) => void
  abort: () => void
}


export const aiApi = {
  chat(message: string, projectId?: string): ChatStreamController {
    const abortController = new AbortController()
    let messageCallback: ((text: string) => void) | null = null
    let doneCallback: (() => void) | null = null
    let errorCallback: ((error: Error) => void) | null = null

    const startStream = async () => {
      try {
        const headers = await getAuthHeadersForStream()
        headers['Content-Type'] = 'application/json'

        const response = await fetch(`${API_BASE}/ai/chat`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ message, projectId }),
          signal: abortController.signal,
        })

        if (!response.ok) {
          const errText = await response.text().catch(() => 'Unknown error')
          throw new Error(`AI Chat Error ${response.status}: ${errText}`)
        }

        const reader = response.body?.getReader()
        if (!reader) throw new Error('No response body')

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              if (data === '[DONE]') {
                doneCallback?.()
                return
              }
              try {
                const parsed = JSON.parse(data)
                if (parsed.content) {
                  messageCallback?.(parsed.content)
                }
              } catch {
                messageCallback?.(data)
              }
            }
          }
        }

        doneCallback?.()
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          errorCallback?.(err as Error)
        }
      }
    }

    setTimeout(startStream, 0)

    return {
      onMessage(callback: (text: string) => void) {
        messageCallback = callback
      },
      onDone(callback: () => void) {
        doneCallback = callback
      },
      onError(callback: (error: Error) => void) {
        errorCallback = callback
      },
      abort() {
        abortController.abort()
      },
    }
  },

  async photoExtract(file: File): Promise<PhotoExtractResult> {
    return apiPostFile<PhotoExtractResult>('/ai/photo-extract', file)
  },

  async generateChecklist(params: {
    equipmentType: string
    systemType: string
    description?: string
    phase: string
  }): Promise<ChecklistGenerateResult> {
    return apiPost<ChecklistGenerateResult>('/ai/generate-checklist', params)
  },

  async getProjectInsights(projectId: string): Promise<ProjectInsights> {
    return apiPost<ProjectInsights>('/ai/project-insights', { projectId })
  },

  async documentQA(question: string, projectId: string): Promise<DocumentQAResult> {
    return apiPost<DocumentQAResult>('/ai/document-qa', { question, projectId })
  },

  async parseDocument(file: File): Promise<ParseDocumentResult> {
    return apiPostFile<ParseDocumentResult>('/ai/parse-document', file)
  },

  async createFromProposal(proposal: ProjectProposal): Promise<CreateFromProposalResult> {
    return apiPost<CreateFromProposalResult>('/ai/create-from-proposal', { proposal })
  },

  async generateDocument(content: string, format: string, filename: string): Promise<Blob> {
    const headers = await getAuthHeadersForStream()
    headers['Content-Type'] = 'application/json'

    const response = await fetch(`${API_BASE}/ai/generate-document`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ content, format, filename }),
    })

    if (!response.ok) {
      const errText = await response.text().catch(() => 'Unknown error')
      throw new Error(`Document generation failed: ${errText}`)
    }

    return response.blob()
  },
}
